// Shared paginated reads for the report layer. EXTRACTED, not reimplemented — this is the loop
// the old PropertyExecutiveSummary page ran, lifted so every report definition shares one copy.
//
// ⛔ WHY EVERY READ MUST LOOP. PostgREST caps a response at 1000 rows (db-max-rows), and
// `.limit(N)` above that is SILENTLY OVERRULED — `.limit(4000)` returns exactly 1000, no error, no
// warning. That is worse than forgetting to paginate: the code explicitly asked for more and was
// ignored, so any comment claiming "we're under the cap" is actively false. This belongs to the
// same silent-success family as an RLS-blocked read (0 rows, no error) and an unchecked write:
// all of them "succeed" and return wrong or partial data. Never treat the absence of an error as
// proof of completeness.
//
// ⛔ ESPECIALLY on the versioned tables. `collectors` and `assessors` are roughly 8x their entity
// count (~1816 active rows); Illinois alone is ~816. A bare `.eq('state', …)` is NOT reliably safe.

import { supabase } from '../supabase';

export const PAGE = 1000;      // PostgREST's hard ceiling — not a tunable
export const ID_CHUNK = 100;   // ids per .in() — keeps the request URL under the length limit

// Paginated fetch. `applyFilter` receives the query builder and returns it with filters attached.
//   fetchAll('properties', 'id,name', q => q.eq('client_id', id))
export async function fetchAll(table, cols, applyFilter = (q) => q) {
  const out = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await applyFilter(supabase.from(table).select(cols)).range(from, from + PAGE - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return out;
}

// Fetch rows whose `column` is in `ids`, chunking the .in() and paginating each chunk.
// Both loops are needed: chunking bounds the URL, paging bounds the response.
export async function fetchByIds(table, cols, column, ids, applyFilter = (q) => q) {
  const unique = [...new Set((ids || []).filter((v) => v != null && v !== ''))];
  if (!unique.length) return [];
  const out = [];
  for (let i = 0; i < unique.length; i += ID_CHUNK) {
    const chunk = unique.slice(i, i + ID_CHUNK);
    out.push(...await fetchAll(table, cols, (q) => applyFilter(q.in(column, chunk))));
  }
  return out;
}

// The common case: a per-parcel table keyed on parcel_id.
export const fetchByParcelIds = (table, cols, ids, applyFilter) =>
  fetchByIds(table, cols, 'parcel_id', ids, applyFilter);

// ⛔ BOUNDED PARALLELISM. Independent per-year lookups should not be awaited one at a time — six
// serialised round trips is most of a report's latency — but firing all of them at once is the
// wrong answer at the other end: a client with twenty years, or several reports open, would open
// twenty-plus connections and the browser would queue them anyway, unpredictably.
//
// CAP = 4. Below the 6-connection-per-host ceiling browsers impose on HTTP/1.1, so the report never
// monopolises the page's budget even if the transport falls back from HTTP/2. Four covers today's
// six-year span in two waves instead of six trips, and stays flat as the span grows.
//
// ⛔ ORDER-PRESERVING: results come back indexed by input position, never by completion order. A
// parallel loop that reorders its results is how "faster" quietly becomes "different".
export const FETCH_CONCURRENCY = 4;

export async function mapWithConcurrency(items, fn, limit = FETCH_CONCURRENCY) {
  const list = items || [];
  const out = new Array(list.length);
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= list.length) return;
      out[i] = await fn(list[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
  return out;
}

// Index a row list by a key, or group it. Small, but every report does one of the two and they
// should not each hand-roll a reduce.
export function indexBy(rows, keyFn) {
  const m = new Map();
  (rows || []).forEach((r) => m.set(keyFn(r), r));
  return m;
}

export function groupBy(rows, keyFn) {
  const m = new Map();
  (rows || []).forEach((r) => {
    const k = keyFn(r);
    const list = m.get(k);
    if (list) list.push(r); else m.set(k, [r]);
  });
  return m;
}
